import { SelectedProductService } from '@/core//services/selectedProductService';
import { instantFilterHasCover, instantFilterPackageHasFriendlySettlementCover } from '@/core/helpers';
import { AutonomousProductsService } from '@/core/services/autonomousProductsService';
import { PackageService } from '@/core/services/packageService';
import {
  DeductionOptions,
  ExtraFilters,
  InsuranceCovers,
  InsuranceTypes,
  Sorting
} from '@/enums/filters';
import { useAPIStore } from '@/stores/api';
import { useQuoteStore } from '@/stores/quote';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const usePackagesStore = defineStore('packages', () => {
  const quoteStore = useQuoteStore();

  // State
  const packages = ref([]);
  const covers = ref({});
  const socket = ref(null);
  const totalNumberOfPackages = ref(0);
  const sorting = ref(Sorting.ByPrice);
  const packagesWithErrors = ref([]);
  const filteredOutPackages = ref([]);
  const APIStore = useAPIStore();
  const autonomousProducts = ref();
  const selectedProductService = SelectedProductService();
  const autonomousProductsService = AutonomousProductsService();
  const packageService = PackageService();

  const SHORT_DRIVE_PACKAGE_IDS = ["124", "125", "146"];
  const shortDriveIdSet = new Set(SHORT_DRIVE_PACKAGE_IDS.map(String));

  // Memoized computed property for filtered and sorted packages
  const filteredAndSortedPackages = computed(() => {
    if (packages.value.length === 0) return [];

    const filteredAndSortedArray = packages.value
      .filter(filterPackages)
      .sort(sortingFunctions[sorting.value]);

    return filteredAndSortedArray;
  });

  const addPackage = (packagePayload) => {
    // We should transform the data here to match the data structure of the packages
    let transformedPackage;
    try {
      transformedPackage = packageService.transformPackage(
        packagePayload,
        quoteStore.selectedFilters?.insuranceDuration,
        quoteStore.selectedFilters?.startingDate,
        quoteStore.quote.quoteData?.selectedFeatures || {},
        autonomousProducts.value, proxyFeatureHandler
      );
    } catch (error) {
      transformedPackage = {
        hasError: true,
        errorMessage: `[Internal Transform Failed] ${error.message}`,
      };
    }

    // If there is an error in the package, we should not add it to the packages array
    if (transformedPackage.hasError) {
      handlePackageError(`There is an error in the package: ${transformedPackage.errorMessage} with id ${packagePayload.multiproduct._id}`);

      // Add the package to the packagesWithErrors array
      packagesWithErrors.value.push({
        id: packagePayload.multiproduct._id,
        errorMessage: transformedPackage.errorMessage,
        company: transformedPackage?.company?.name,
        payload: packagePayload,
      });
      return false;
    }

    // If the package does not include all the covers selected by the user, we should not add it to the packages array
    if (!allSelectedCoversIncluded(transformedPackage)) {
      handlePackageError(`The package ${transformedPackage.packageId} does not include all the selected covers`, transformedPackage.includedCoversFlat);
      return false;
    }

    if (transformedPackage.startDateFromProduct === true && transformedPackage.company?.id && transformedPackage.packageId) {
      selectedProductService.cacheStartDateConstraint(transformedPackage.company.id, transformedPackage.packageId, transformedPackage.startDate);
    }

    packages.value.push(transformedPackage);
    return true
  };

  // Log the error and adjust total number of packages
  function handlePackageError(errorMessage) {
    totalNumberOfPackages.value -= 1;
  }

  const allSelectedCoversIncluded = (packageItem) => {
    const selectedCoversByUser = quoteStore.appliedFilters.insuranceCovers;

    const hasAllCovers = selectedCoversByUser.every((selectedCover) =>
      packageItem.includedCoversFlat.includes(selectedCover)
    );

    return hasAllCovers;

  };

  const setExpectedNumberOfPackages = (expectedNumberOfPackages) => {
    totalNumberOfPackages.value = expectedNumberOfPackages;
  }

  const setAutonomousCovers = autoProducts => {
      autonomousProducts.value = autoProducts
      autonomousProductsService.storePrices(autoProducts);
  }

  const setExpectedNumberOfPackagesWhenWSCloses = _ => totalNumberOfPackages.value = packages.value.length

  const allPackagesReceived = () => {
    return packages.value.length === totalNumberOfPackages.value;
  };


  // Merge filter functions into one
  const filterPackages = (packageItem) => {
    if ( quoteStore.appliedFilters.insuranceType === InsuranceTypes.Extra)
      return filterPackageByExtraFilters(packageItem) && filterPackageByDeductionOptionsTotalTheft(packageItem);
    else if ( quoteStore.appliedFilters.insuranceType === InsuranceTypes.Premium)
      return filterPackageByExtraFilters(packageItem) && filterPackageByDeductionOptionsOwnDamages(packageItem);
    else return filterPackageByExtraFilters(packageItem)
  };

  const filterPackageByExtraFilters = (packageItem) => {
    const appliedExtraFilters = quoteStore.appliedFilters.extraFilters;

    let includeBasic = false;
    let includeExtra = false;
    let includePremium = false;

    const wantsShortDrive = appliedExtraFilters.includes(ExtraFilters.ShortDrive);
    const isShortDriveId  = shortDriveIdSet.has(String(packageItem.packageId));

    const passesShortDrive = wantsShortDrive ? isShortDriveId : !isShortDriveId;

    const passesFriendlySettlement = !appliedExtraFilters.includes(ExtraFilters.FriendlySettlement) ||
        (instantFilterPackageHasFriendlySettlementCover(packageItem, InsuranceCovers.FriendlySettlement));

    if (
        !appliedExtraFilters.includes(ExtraFilters.BasicNaturalPhenomena) &&
        !appliedExtraFilters.includes(ExtraFilters.ExtraNaturalPhenomena) &&
        !appliedExtraFilters.includes(ExtraFilters.PremiumNaturalPhenomena)
    ) {
      includeBasic = includeExtra = includePremium = true;
    }

    if (appliedExtraFilters.includes(ExtraFilters.BasicNaturalPhenomena)) {
      includeBasic = !(
          instantFilterHasCover(packageItem, InsuranceCovers.SnowDamage) ||
          instantFilterHasCover(packageItem, InsuranceCovers.Volcano) ||
          instantFilterHasCover(packageItem, InsuranceCovers.HailDamage)
      );
    }

    if (appliedExtraFilters.includes(ExtraFilters.ExtraNaturalPhenomena)) {
      includeExtra =
          (instantFilterHasCover(packageItem, InsuranceCovers.SnowDamage) ||
              instantFilterHasCover(packageItem, InsuranceCovers.Volcano)) &&
          !instantFilterHasCover(packageItem, InsuranceCovers.HailDamage);
    }

    if (appliedExtraFilters.includes(ExtraFilters.PremiumNaturalPhenomena)) {
      includePremium = instantFilterHasCover(packageItem, InsuranceCovers.HailDamage);
    }

    if (
        !((!appliedExtraFilters.includes(ExtraFilters.EasyBuy) || packageItem.easyBuy) &&
            (!appliedExtraFilters.includes(ExtraFilters.Offers) ||
                (packageItem.hasOffer && packageItem.mainBanners[0].type === "Discounts")) &&
            (includeBasic || includeExtra || includePremium) &&
            passesShortDrive &&
            passesFriendlySettlement)
    )
      filteredOutPackages.value.push({
        id: packageItem._id,
        errorMessage: 'The package does not pass the extra filters',
        company: packageItem?.company?.name,
        price: packageItem?.price,
        payload: packageItem.payload,
      })

    return (
        (!appliedExtraFilters.includes(ExtraFilters.EasyBuy) || packageItem.easyBuy) &&
        (!appliedExtraFilters.includes(ExtraFilters.Offers) ||
            (packageItem.hasOffer && packageItem.mainBanners[0].type === "Discounts")) &&
        (includeBasic || includeExtra || includePremium) &&
        passesShortDrive &&
        passesFriendlySettlement
    );
  };

  const filterPackageByDeductionOptionsTotalTheft = (packageItem) => {
    const appliedDeductionOptions = quoteStore.appliedFilters.deductionOptions;
    if ( !(
      appliedDeductionOptions.length === 0 ||
      appliedDeductionOptions.some((deductionOption) =>
        DeductionOptions[deductionOption].filter(packageItem.totalTheftDeductible)
      )
    )) {
      filteredOutPackages.value.push({
        id: packageItem._id,
        errorMessage: 'The package does not pass the deduction filters',
        company: packageItem?.company?.name,
        price: packageItem?.price,
        payload: packageItem.payload,
      })
    }

    return (
      appliedDeductionOptions.length === 0 ||
      appliedDeductionOptions.some((deductionOption) =>
        DeductionOptions[deductionOption].filter(packageItem.totalTheftDeductible)
      )
    );
  };

  const filterPackageByDeductionOptionsOwnDamages = (packageItem) => {
    const appliedDeductionOptions = quoteStore.appliedFilters.deductionOptions;
    if (!(
        appliedDeductionOptions.length === 0 ||
        appliedDeductionOptions.some((deductionOption) =>
            DeductionOptions[deductionOption].filter(packageItem.ownDamagesDeductible)
        )
    )) {
      filteredOutPackages.value.push({
        id: packageItem._id,
        errorMessage: 'The package does not pass the deduction filters',
        company: packageItem?.company?.name,
        price: packageItem?.price,
        payload: packageItem.payload,
      })
    }

    return (
        appliedDeductionOptions.length === 0 ||
        appliedDeductionOptions.some((deductionOption) =>
            DeductionOptions[deductionOption].filter(packageItem.ownDamagesDeductible)
        )
    );
  };

  // Object mapping Sorting values to sorting functions
  const sortingFunctions = {
    [Sorting.ByPrice]: (a, b) => parseFloat(a.price) - parseFloat(b.price),
    [Sorting.ByBenefits]: (a, b) =>
      parseFloat(b.numberOfCovers) - parseFloat(a.numberOfCovers),
    [Sorting.ByRating]: (a, b) => {
      let companyARating = parseFloat(a.company.rating);
      let companyBRating = parseFloat(b.company.rating);

      if (isNaN(companyARating)) {
        companyARating = 0;
      }
      if (isNaN(companyBRating)) {
        companyBRating = 0;
      }
      return parseFloat(companyBRating) - parseFloat(companyARating);
    },
  };

  const reset = () => {
    totalNumberOfPackages.value = 0;
    packages.value = [];
    packagesWithErrors.value = [];
    filteredOutPackages.value = [];
  };

  const getPackage =  async (packageId) => {
    let pack = packages.value.find((packageItem) => packageItem.id === packageId);

    const populateFeature = async (feature)=>{
      const cover = await getCover(feature._id);
      if(cover) {
        feature.description = cover.description;
        feature.attributes.terms = cover.attributes.terms;
        feature.attributes.limitations = cover.attributes.limitations;
      }
      return feature;
    }

    const promises = [];
    pack.payload.multiproduct.products.forEach((product, idx) => {
      product.features.forEach((feature, fidx) => {
        promises.push(populateFeature(feature).then((newFeature) => {
          pack.payload.multiproduct.products[idx].features[fidx] = newFeature;
        }));
      });
    });

    await Promise.all(promises);

    return pack;
  }

  // add the cover by id on Covers map
  const addCover = (cover_id,cover) => {
    // Save the Cover  for future use
    covers.value[cover_id] = cover;
  };

  const getCover = async (cover_id)=>{
    let cover = covers.value[cover_id] ;
    if(!cover) {
      cover = await APIStore.getCover(cover_id);
    }
    return cover;
  }

  const proxyFeatureHandler = {
    get:  function (target, prop, receiver) {
      if (prop === 'description') {
        return new Promise(async (resolve) => {
          const cover = await getCover(target._id);
          resolve(cover.description)
        })
      } else  if (prop === 'attributes') {
        return new Proxy(target.attributes, {
          get: function (target2, prop2) {
            if (prop2 === 'terms') {
              return new Promise(async (resolve) => {
                const cover = await getCover(target._id);
                resolve(cover.attributes.terms)
              })
            } else  if (prop2 === 'limitations') {
              return new Promise(async (resolve) => {
                const cover = await getCover(target._id);
                resolve(cover.attributes.limitations)
              })
            }
            return Reflect.get(...arguments);
          }
        });
      }
      return Reflect.get(...arguments);
    },
  };

  const getPackagePosition = (packet) => {
    return filteredAndSortedPackages.value.findIndex((item) => item.id === packet?.id) + 1;
  }

  const storeSelectedProduct = (multiproduct, offerCode, dc) => {
    selectedProductService.store(multiproduct, offerCode, dc)
  };

  return {
    packages,
    packagesWithErrors,
    getPackage,
    addPackage,
    allPackagesReceived,
    autonomousProducts,
    setExpectedNumberOfPackagesWhenWSCloses,
    filteredOutPackages,
    socket,
    totalNumberOfPackages,
    sorting,
    reset,
    proxyFeatureHandler,
    filteredAndSortedPackages,
    setExpectedNumberOfPackages,
    covers,
    addCover,
    setAutonomousCovers,
    storeSelectedProduct,
    getPackagePosition
  };
});

